import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { TrendingDown } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';

const CalorieComparison = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} data-testid="calorie-comparison-section" className="py-24 px-6 bg-[#0A0A0A] border-t border-[#2A2A2A]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-[#FF3D00] text-black px-3 py-1 font-['Outfit'] font-bold uppercase text-xs tracking-[0.2em] mb-6">
            <TrendingDown size={14} />
            The Big Cut
          </div>
          <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-white uppercase leading-none mb-4" data-testid="calorie-heading">
            Half the calories.<br /><span className="text-[#FF3D00]">Same biscuit.</span>
          </h2>
          <p className="font-['Manrope'] text-base sm:text-lg text-white/70 max-w-2xl mx-auto">
            A regular cream biscuit pack hits you with ~500 kcal. We're cutting that by more than half — targeting under 200 kcal per pack.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
          {/* The Other Guys */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="bg-black/40 border border-[#2A2A2A] p-8 sm:p-10 relative overflow-hidden"
            data-testid="other-guys-card"
          >
            <div className="absolute top-4 right-4 text-white/20 font-['Bebas_Neue'] text-7xl select-none rotate-12">
              YIKES
            </div>
            <div className="text-white/40 font-['Outfit'] text-xs uppercase tracking-[0.3em] mb-6">
              Regular Cream Biscuit Pack
            </div>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-['Bebas_Neue'] text-7xl sm:text-8xl text-white/40">
                <AnimatedCounter value={500} duration={1.5} className="" testId="other-kcal-counter" />
              </span>
              <span className="font-['Bebas_Neue'] text-2xl text-white/40">kcal</span>
            </div>
            <div className="h-3 bg-white/5 mb-3 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={inView ? { width: '100%' } : {}}
                transition={{ duration: 1.4, delay: 0.3, ease: 'easeOut' }}
                className="h-full bg-white/30"
              />
            </div>
            <ul className="space-y-2 mt-6 font-['Manrope'] text-sm text-white/50">
              <li>Loaded with palm oil</li>
              <li>Refined sugar everywhere</li>
              <li>Empty calories. No protein.</li>
              <li>Made you crave more, faster.</li>
            </ul>
          </motion.div>

          {/* Way Big */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="bg-[#FF3D00] text-black p-8 sm:p-10 relative overflow-hidden border-2 border-[#FF3D00]"
            style={{ boxShadow: '8px 8px 0px #000' }}
            data-testid="waybig-card"
          >
            <div className="absolute top-4 right-4 text-black/10 font-['Bebas_Neue'] text-7xl select-none -rotate-12">
              WIN
            </div>
            <div className="text-black/70 font-['Outfit'] text-xs uppercase tracking-[0.3em] mb-6 flex items-center gap-2">
              <span className="bg-black text-[#FFB300] px-2 py-0.5">WAY BIG</span> Cream Biscuit Pack
            </div>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-['Bebas_Neue'] text-7xl sm:text-8xl text-black">
                {'<'}<AnimatedCounter value={200} duration={1.8} className="" testId="waybig-kcal-counter" />
              </span>
              <span className="font-['Bebas_Neue'] text-2xl text-black">kcal</span>
            </div>
            <div className="h-3 bg-black/10 mb-3 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={inView ? { width: '40%' } : {}}
                transition={{ duration: 1.4, delay: 0.5, ease: 'easeOut' }}
                className="h-full bg-black"
              />
            </div>
            <ul className="space-y-2 mt-6 font-['Manrope'] text-sm text-black/80 font-medium">
              <li>10g protein per pack</li>
              <li>3g fibre — actually fills you up</li>
              <li>No palm oil. No refined sugar.</li>
              <li>Snack that doesn't hijack your day.</li>
            </ul>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-10 font-['Manrope'] text-sm text-white/40 max-w-2xl mx-auto"
        >
          *500 kcal based on typical 100g cream biscuit packs sold in India. Way Big targets under 200 kcal per pack — final number locked at launch.
        </motion.p>
      </div>
    </section>
  );
};

export default CalorieComparison;
